/** SUNTRONIX ONE — Universal AI Product Description Engine.
 *  Technology → Description Enrichment. Auto-writes concise commercial quote
 *  descriptions from verified facts (never invents specs). Edit/regenerate,
 *  manual edits are protected from future overwrite.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Loader2, PlayCircle, RefreshCw, Gauge, Wand2, Pencil, Check, X } from "lucide-react";

const TONE = {
  AI_GENERATED: "bg-sky-100 text-sky-800 border-sky-200",
  MANUALLY_VERIFIED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  READY: "bg-emerald-100 text-emerald-800 border-emerald-200",
  REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
  MISSING: "bg-rose-100 text-rose-700 border-rose-200",
};
const FILTERS = ["", "MISSING", "AI_GENERATED", "MANUALLY_VERIFIED", "REVIEW"];

function Cov({ label, value, tone = "text-slate-900", icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-medium text-slate-500 flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</div>
      <div className={"text-[19px] font-bold mt-0.5 tabular-nums " + tone}>{value ?? "—"}</div>
    </div>
  );
}

function Row({ p, onGenerate, onSave, busy }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(p.commercial_description || "");
  const [proposal, setProposal] = useState(null);
  useEffect(() => { setText(p.commercial_description || ""); }, [p.commercial_description]);

  const gen = async () => {
    const res = await onGenerate(p);
    if (res?.proposed) { setProposal(res.proposed); setEditing(true); }
  };
  return (
    <div className="border-b border-slate-100 p-3" data-testid={`desc-row-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-slate-800 truncate" title={p.product_name}>{p.product_name}</div>
          <div className="text-[11px] text-slate-400">{p.brand || "—"}{p.model ? ` · ${p.model}` : ""}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Badge className={"text-[10px] border " + (TONE[p.description_status] || TONE.MISSING)}>
            {(p.description_status || "MISSING").replace(/_/g, " ")}
          </Badge>
          {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
            <>
              <button onClick={gen} title="Regenerate with AI" data-testid={`desc-generate-${p.id}`}
                className="p-1.5 rounded hover:bg-indigo-50 text-indigo-500"><Wand2 className="h-3.5 w-3.5" /></button>
              <button onClick={() => setEditing((e) => !e)} title="Edit" data-testid={`desc-edit-${p.id}`}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><Pencil className="h-3.5 w-3.5" /></button>
            </>
          )}
        </div>
      </div>
      {!editing && p.commercial_description && (
        <p className="text-[12px] text-slate-600 mt-1.5 leading-snug">{p.commercial_description}</p>
      )}
      {editing && (
        <div className="mt-2 space-y-2">
          {proposal && proposal !== text && (
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-2 text-[12px]">
              <div className="text-[10px] uppercase text-sky-600 font-semibold mb-1">AI proposal</div>
              <p className="text-slate-700">{proposal}</p>
              <div className="mt-1.5 flex gap-2">
                <Button size="sm" className="h-7 text-xs bg-slate-900 text-white" data-testid={`desc-accept-${p.id}`}
                  onClick={() => { setText(proposal); setProposal(null); }}>
                  <Check className="h-3 w-3 mr-1" />Use proposal
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setProposal(null)}>
                  <X className="h-3 w-3 mr-1" />Keep current
                </Button>
              </div>
            </div>
          )}
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
            data-testid={`desc-textarea-${p.id}`} className="text-[12.5px]" placeholder="Commercial quote description…" />
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`desc-save-${p.id}`} onClick={async () => { await onSave(p, text); setEditing(false); }}>
              Save (verified)
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(false); setProposal(null); setText(p.commercial_description || ""); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DescriptionEnrichment() {
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("MISSING");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const poll = useRef(null);

  const loadOverview = useCallback(() => api.get("/description-enrichment/overview").then(({ data }) => setOv(data)).catch(() => {}), []);
  const loadRows = useCallback(() => {
    setLoading(true);
    api.get("/description-enrichment/products", { params: { status: status || undefined, q: q || undefined, page_size: 40 } })
      .then(({ data }) => setRows(data.items || []))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, [status, q]);

  useEffect(() => { loadOverview(); api.get("/description-enrichment/scan/job").then(({ data }) => setJob(data.job)).catch(() => {}); }, [loadOverview]);
  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    if (job?.status === "RUNNING") {
      poll.current = setInterval(async () => {
        try {
          const { data } = await api.get("/description-enrichment/scan/job");
          setJob(data.job);
          if (data.job?.status !== "RUNNING") { clearInterval(poll.current); loadOverview(); loadRows(); }
        } catch { /* ignore */ }
      }, 2500);
      return () => clearInterval(poll.current);
    }
  }, [job?.status, loadOverview, loadRows]);

  const backfill = async () => {
    try {
      const { data } = await api.post("/description-enrichment/generate-missing", { mode: "STANDARD", limit: 0 });
      if (data.already_running) toast.info("A backfill is already running");
      else toast.success("AI description backfill started");
      const { data: j } = await api.get("/description-enrichment/scan/job");
      setJob(j.job);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };

  const generate = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/description-enrichment/${p.id}/generate`, { mode: "STANDARD", save: false });
      if (data.status === "REVIEW") toast.warning(data.reason || "Needs review");
      else toast.success("AI proposal ready");
      return data;
    } catch (e) { toast.error(e.response?.data?.detail || "Generate failed"); return null; }
    finally { setBusy(null); }
  };

  const save = async (p, text) => {
    if (!text?.trim()) { toast.error("Description required"); return; }
    setBusy(p.id);
    try {
      await api.post(`/description-enrichment/${p.id}/set`, { description: text, reason: "Manual edit" });
      toast.success("Saved — manually verified (protected from AI overwrite)");
      loadOverview(); loadRows();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(null); }
  };

  const running = job?.status === "RUNNING";

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="description-enrichment-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center text-white"><FileText className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>AI Product Descriptions</h1>
          <p className="text-[13px] text-slate-500">Concise commercial quote descriptions from verified facts. Never invents specs. Manual edits are protected.</p>
        </div>
      </div>

      {ov && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="desc-overview">
          <Cov label="Coverage" value={`${ov.coverage_pct}%`} tone="text-emerald-700" icon={Gauge} testid="desc-cov-pct" />
          <Cov label="Ready" value={ov.ready} tone="text-emerald-700" testid="desc-cov-ready" />
          <Cov label="Missing" value={ov.missing} tone="text-rose-700" testid="desc-cov-missing" />
          <Cov label="AI generated" value={ov.ai_generated} tone="text-sky-700" testid="desc-cov-ai" />
          <Cov label="Verified" value={ov.manually_verified} tone="text-emerald-700" testid="desc-cov-verified" />
          <Cov label="Review" value={ov.review} tone="text-amber-700" testid="desc-cov-review" />
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
        <span className="text-[13px] text-slate-600">Generate concise descriptions for every product missing one.</span>
        <div className="flex-1" />
        <Button disabled={running} onClick={backfill} data-testid="desc-backfill" className="bg-slate-900 hover:bg-slate-800 text-white">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PlayCircle className="h-4 w-4 mr-1.5" />}
          Generate all missing
        </Button>
        <Button variant="outline" onClick={() => { loadOverview(); loadRows(); }} data-testid="desc-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {job && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="desc-job">
          <div className="flex items-center justify-between text-sm">
            <span className="font-mono font-semibold">{job.id}
              <span className={"ml-2 text-[11px] font-semibold " + (running ? "text-amber-600" : job.status === "DONE" ? "text-emerald-600" : "text-rose-600")} data-testid="desc-job-status">
                {running ? "GENERATING" : job.status}
              </span>
            </span>
            <span className="text-slate-500 text-xs">{job.processed || 0} / {job.total || 0}</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={"h-full transition-all " + (running ? "bg-sky-500" : "bg-emerald-500")}
              style={{ width: `${job.total ? Math.round((job.processed / job.total) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
        <Input data-testid="desc-search" value={q} onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[200px]" placeholder="Search name, brand, model…" />
        {FILTERS.map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`desc-filter-${s || "all"}`}
            className={"text-xs px-3 py-1.5 rounded-full border transition-colors " +
              (status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            {s ? s.replace(/_/g, " ") : "All"}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="desc-list">
        {loading && <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}
        {!loading && rows.length === 0 && <div className="p-8 text-center text-slate-400">No products in this state 🎉</div>}
        {!loading && rows.map((p) => <Row key={p.id} p={p} onGenerate={generate} onSave={save} busy={busy} />)}
      </div>
    </div>
  );
}
